// src/V2/helpers/international.ts
import store from "@/V2/store/index"
import axios from 'axios';
import Vue from 'vue';
import { getSessionToken } from '@shopify/app-bridge-utils';

type CacheValue = { country_of_origin: string | null; hs_code: string | null };

// Tune if needed
const BATCH_SIZE = 250;

const VARIANT_BATCH_QUERY = `
  query VariantBatch($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        inventoryItem {
          countryCodeOfOrigin
          harmonizedSystemCode
          countryHarmonizedSystemCodes(first: 250) {
            edges {
              node {
                countryCode
                harmonizedSystemCode
              }
            }
          }
        }
      }
    }
  }
`;

function collectVariantNeeds(orders: any[]) {
  // Map<variantId, Set<countryCode>>
  const needsByVariant = new Map<string, Set<string>>();
  for (const order of orders || []) {
    const shipCountry: string | null = order?.shipping_address?.country_code || null;
    for (const li of order?.line_items || []) {
      const id = li.variant_id;
      if (!id) continue;
      const key = String(id);
      if (!needsByVariant.has(key)) needsByVariant.set(key, new Set());
      if (shipCountry) needsByVariant.get(key)!.add(shipCountry);
    }
  }
  return needsByVariant;
}

function pickVariantsToFetch(
  needsByVariant: Map<string, Set<string>>,
  cache: Record<string, CacheValue>
) {
  const toFetch: string[] = [];
  for (const [variantId, countries] of needsByVariant) {
    const globalKey = `${variantId}:GLOBAL`;
    let need = !cache[globalKey]; // if no GLOBAL, fetch
    if (!need) {
      for (const cc of countries) {
        if (!cache[`${variantId}:${cc}`]) { need = true; break; }
      }
    }
    if (need) toFetch.push(variantId);
  }
  return toFetch;
}

async function fetchAndWarmVariants(
  variantIds: string[],
  app: any,
  cache: Record<string, CacheValue>
) {
  for (let i = 0; i < variantIds.length; i += BATCH_SIZE) {
    const chunk = variantIds.slice(i, i + BATCH_SIZE);
    const ids = chunk.map(id => `gid://shopify/ProductVariant/${id}`);

    const token = await getSessionToken(app);
    const resp = await axios.post(
      `${process.env.VUE_APP_V2_SHOPIFY_API}/graphql`,
      { graphql: { query: VARIANT_BATCH_QUERY, variables: { ids } } },
      { headers: { Authorization: token } }
    );
    store.commit('set_loading', { status: true, message: 'Retrieving international data', step: 'International', count: variantIds.length - i}, {root: true })
    const nodes = resp.data?.data?.nodes || [];
    for (const node of nodes) {
      if (!node?.inventoryItem) continue;

      const variantGid: string = node.id; // gid://shopify/ProductVariant/123
      const variantId = variantGid.split('/').pop()!;
      const inv = node.inventoryItem;

      const baseOrigin = inv.countryCodeOfOrigin ?? null;
      const globalHS   = inv.harmonizedSystemCode ?? null;

      // GLOBAL cache
      Vue.set(cache, `${variantId}:GLOBAL`, {
        country_of_origin: baseOrigin,
        hs_code: globalHS,
      });

      // Country overrides
      const edges = inv.countryHarmonizedSystemCodes?.edges ?? [];
      for (const e of edges) {
        const cc  = e?.node?.countryCode;
        const hsc = e?.node?.harmonizedSystemCode ?? null;
        if (cc) {
          Vue.set(cache, `${variantId}:${cc}`, {
            country_of_origin: baseOrigin,
            hs_code: hsc,
          });
        }
      }
      
    }

    // (Optional) throttle slightly
    // await new Promise(r => setTimeout(r, 120));
  }
}

/**
 * Public helper:
 * - orders: list of orders (already filtered to international if you want)
 * - app: rootGetters['Shopify/app']
 * - cache: your reactive cache object (e.g. state.international_data_cache)
 */
export async function hydrateInternationalDataForOrders(
  orders: any[],
  app: any,
  cache: Record<string, CacheValue>
): Promise<boolean> {
  if (!orders?.length) return true;

  // 1) Collect all needs from orders
  const needsByVariant = collectVariantNeeds(orders);
  // 2) Identify variants we still need to fetch
  const toFetch = pickVariantsToFetch(needsByVariant, cache);
  store.commit('set_loading', { status: true, message: 'Retrieving international data', step: 'International', count: toFetch.length }, {root: true })

  // 3) Fetch variants in batches and warm the cache (GLOBAL + overrides)
  if (toFetch.length) {
    await fetchAndWarmVariants(toFetch, app, cache);
  }

  // 4) Apply from cache to each line item
  for (const order of orders || []) {
    const shipCountry = order?.shipping_address?.country_code || null;
    for (const li of order?.line_items || []) {
      const id = li.variant_id;
      if (!id) continue;
      const best =
        (shipCountry && cache[`${id}:${shipCountry}`]) ||
        cache[`${id}:GLOBAL`] ||
        null;
      if (best) {
        Vue.set(li, 'country_of_origin', best.country_of_origin);
        Vue.set(li, 'hs_code', best.hs_code);
      }
    }
  }

  return true;
}
